import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Building2, ArrowRight, Lock, User, AlertCircle, CheckCircle2, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role labels mapping
const ROLE_LABELS = {
  director: 'Director',
  bd_manager: 'Business Development Manager',
  senior_engineer: 'Senior Engineer',
  associate_engineer: 'Associate Engineer',
  engineer: 'Engineer'
};

export const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: ''
  });
  const [consent, setConsent] = useState(false);
  const { login: _login } = useAuth();
  const _navigate = useNavigate();

  useEffect(() => {
    const validateInvite = async () => {
      if (!token) {
        setError('Invalid invite link. No token provided.');
        setValidating(false);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/validate-invite/${token}`);
        const data = await response.json();
        
        if (data.valid) {
          setInviteData(data);
          setFormData(prev => ({
            ...prev,
            first_name: data.first_name || '',
            last_name: data.last_name || ''
          }));
        } else {
          setError(data.error || 'Invalid or expired invite');
        }
      } catch (err) {
        setError('Failed to validate invite. Please try again.');
      } finally {
        setValidating(false);
        setLoading(false);
      }
    };

    validateInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!consent) {
      setError('Please accept the data processing policy to continue');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/register-with-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          first_name: formData.first_name,
          last_name: formData.last_name,
          password: formData.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create account');
      }

      // Store token and redirect
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Give consent since checkbox was checked
      if (data.requires_consent) {
        await fetch(`${API_URL}/api/auth/consent`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${data.token}`
          }
        });
      }
      
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <p className="text-slate-500">Validating your invite...</p>
        </div>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#1e3a5f] mb-2">
            Invite Invalid
          </h1>
          <p className="text-slate-500 mb-6">{error}</p>
          <Link to="/login">
            <Button className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl">
              Go to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="accept-invite-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8 md:p-16 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#1e3a5f] rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-2xl font-bold text-[#1e3a5f]">FURD OpsFlow</span>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="text-emerald-800 font-medium">You&apos;ve been invited!</p>
                <p className="text-emerald-700 text-sm mt-1">
                  Join <strong>{inviteData?.team_name}</strong> as a <strong>{ROLE_LABELS[inviteData?.role] || inviteData?.role}</strong>
                </p>
              </div>
            </div>
          </div>
          
          <h1 className="font-heading text-3xl font-bold text-[#1e3a5f] mb-2">
            Complete your account
          </h1>
          <p className="text-slate-500 mb-8">
            Create your password to get started
          </p>
          
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Email</Label>
              <Input
                type="email"
                value={inviteData?.email || ''}
                disabled
                className="h-12 rounded-xl bg-slate-100 border-slate-200 text-slate-500"
              />
              <p className="text-xs text-slate-400">Email cannot be changed</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">First name</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder="John"
                    className="pl-11 h-12 rounded-xl bg-slate-50 border-slate-200"
                    data-testid="invite-firstname-input"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Last name</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  placeholder="Smith"
                  className="h-12 rounded-xl bg-slate-50 border-slate-200"
                  data-testid="invite-lastname-input"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Min. 8 characters"
                  className="pl-11 h-12 rounded-xl bg-slate-50 border-slate-200"
                  data-testid="invite-password-input"
                  required
                  minLength={8}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Confirm password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                  placeholder="Confirm your password"
                  className="pl-11 h-12 rounded-xl bg-slate-50 border-slate-200"
                  data-testid="invite-confirm-password-input"
                  required
                />
              </div>
            </div>

            {/* Consent checkbox */}
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
              <Checkbox
                id="consent"
                checked={consent}
                onCheckedChange={setConsent}
                className="mt-0.5"
                data-testid="consent-checkbox"
              />
              <label htmlFor="consent" className="text-sm text-slate-600 cursor-pointer">
                I agree to the{' '}
                <Link to="/privacy-policy" className="text-[#1e3a5f] underline" target="_blank">
                  data processing policy
                </Link>{' '}
                for this workspace. I understand how my data will be collected, stored, and used.
              </label>
            </div>
            
            <Button
              type="submit"
              disabled={loading || !consent}
              className="w-full h-12 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl font-semibold disabled:opacity-50"
              data-testid="invite-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create account'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>
          
          <p className="mt-6 text-center text-slate-500 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-[#1e3a5f] font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
      
      {/* Right - Info */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#1e3a5f] via-[#243f66] to-[#1a3250] items-center justify-center p-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
            {[...Array(20)].map((_, i) => (
              <line key={`h${i}`} x1="0" y1={i * 5} x2="100" y2={i * 5} stroke="white" strokeWidth="0.3" />
            ))}
            {[...Array(20)].map((_, i) => (
              <line key={`v${i}`} x1={i * 5} y1="0" x2={i * 5} y2="100" stroke="white" strokeWidth="0.3" />
            ))}
          </svg>
        </div>
        
        <div className="relative z-10 text-center max-w-lg">
          <div className="w-24 h-24 bg-white/10 rounded-3xl flex items-center justify-center mx-auto mb-8 backdrop-blur-sm">
            <Shield className="w-12 h-12 text-white" />
          </div>
          <h2 className="font-heading text-4xl font-bold text-white mb-4">
            Secure Workspace
          </h2>
          <p className="text-white/70 text-lg mb-8">
            Your data is protected with enterprise-grade security
          </p>
          <div className="grid grid-cols-2 gap-4 text-left">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white font-medium text-sm">AES-256 Encryption</p>
              <p className="text-white/60 text-xs mt-1">Data at rest</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white font-medium text-sm">TLS 1.3</p>
              <p className="text-white/60 text-xs mt-1">Data in transit</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white font-medium text-sm">GDPR Compliant</p>
              <p className="text-white/60 text-xs mt-1">EU data protection</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white font-medium text-sm">Daily Backups</p>
              <p className="text-white/60 text-xs mt-1">30-day retention</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
